import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotificationCenter } from "../context/NotificationContext";

const ALERT_EVENT = "buzzer-klip-live-campaign-alerts";

function storageKey(user) {
  return user?.id ? `bk_live_campaign_seen_${user.id}` : "";
}

function campaignKey(item) {
  return item?.notification_key || item?.campaign_id || item?.id || "";
}

function readSeen(key) {
  if (!key || typeof window === "undefined") return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeSeen(key, ids) {
  if (!key || typeof window === "undefined") return;
  const unique = Array.from(new Set(ids.filter(Boolean))).slice(-200);
  window.localStorage.setItem(key, JSON.stringify(unique));
  window.dispatchEvent(new CustomEvent(ALERT_EVENT, { detail: { key, ids: unique } }));
}

export default function useCreatorLiveCampaignAlerts(user, { limit = 5 } = {}) {
  const isCreator = user?.role === "creator";
  const key = storageKey(user);
  const { live_campaigns: liveCampaignData, refresh: refreshNotifications } = useNotificationCenter();
  const campaigns = useMemo(
    () => isCreator && Array.isArray(liveCampaignData?.campaigns) ? liveCampaignData.campaigns : [],
    [isCreator, liveCampaignData],
  );
  const [seenIds, setSeenIds] = useState(() => readSeen(key));

  useEffect(() => {
    setSeenIds(readSeen(key));
  }, [key]);

  useEffect(() => {
    const syncSeen = (event) => {
      if (event?.detail?.key && event.detail.key !== key) return;
      setSeenIds(readSeen(key));
    };
    window.addEventListener("storage", syncSeen);
    window.addEventListener(ALERT_EVENT, syncSeen);
    return () => {
      window.removeEventListener("storage", syncSeen);
      window.removeEventListener(ALERT_EVENT, syncSeen);
    };
  }, [key]);

  const unseenCampaigns = useMemo(() => {
    const seen = new Set(seenIds);
    return campaigns.filter((item) => !seen.has(campaignKey(item))).slice(0, limit);
  }, [campaigns, seenIds, limit]);

  const markSeen = useCallback((items = campaigns) => {
    const nextIds = [
      ...seenIds,
      ...items.map(campaignKey),
    ];
    writeSeen(key, nextIds);
    setSeenIds(readSeen(key));
  }, [campaigns, key, seenIds]);

  return {
    campaigns,
    unseenCampaigns,
    unseenCount: unseenCampaigns.length,
    markSeen,
    refresh: () => refreshNotifications({ force: true }),
    campaignKey,
  };
}
